import { useEffect, useState, type CSSProperties } from 'react'
import { Box, Text } from '@chakra-ui/react'

interface ImageState {
  url: string | null
  failed: boolean
}

/**
 * Descarga una imagen protegida (Bearer token) y la expone como object URL.
 * Solo llama a setState dentro de callbacks asíncronos / cleanup (no en el
 * cuerpo del efecto) y revoca el object URL anterior con un updater funcional,
 * evitando tanto fugas de memoria como cascadas de render.
 */
function useAuthImageUrl(src: string): ImageState {
  const [state, setState] = useState<ImageState>({ url: null, failed: false })

  useEffect(() => {
    let cancelled = false
    const token = localStorage.getItem('auth_token')

    fetch(src, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => {
        if (cancelled) return
        if (blob) setState({ url: URL.createObjectURL(blob), failed: false })
        else setState({ url: null, failed: true })
      })
      .catch(() => {
        if (!cancelled) setState({ url: null, failed: true })
      })

    return () => {
      cancelled = true
      setState((prev) => {
        if (prev.url) URL.revokeObjectURL(prev.url)
        return { url: null, failed: false }
      })
    }
  }, [src])

  return state
}

interface AuthImageProps {
  src: string
  alt: string
  style?: CSSProperties
  /** Altura mínima del placeholder mientras carga. */
  minHeight?: string
}

export default function AuthImage({ src, alt, style, minHeight = '120px' }: AuthImageProps) {
  const { url, failed } = useAuthImageUrl(src)

  if (!url) {
    return (
      <Box
        style={style}
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="#efe8dd"
        borderRadius="6px"
        minH={minHeight}
      >
        <Text fontSize="xs" color="#999">
          {failed ? 'No disponible' : 'Cargando...'}
        </Text>
      </Box>
    )
  }

  return <img src={url} alt={alt} style={style} />
}
